"use client";

import { useEffect, useRef, useState } from "react";

import { captureEvent } from "@/app/_analytics/analytics";
import { useNativeAudio } from "@/app/_providers/native-audio";
import { EventName } from "@mw/shared/analyticsEventNames";

import type { AlbumCover, SeekPlaybackOptions } from "./AlbumCoverTypes";
import type { HeroMusicTimelineGroupId } from "./HeroMusicTimelineBar";
import { clampValue, getWrappedIndex } from "./HeroMusicUtils";

type ActiveAudioPlayback = {
  coverIndex: number;
  duration: number;
  playbackKey: string;
};

const PLAYBACK_KEY_PREFIX = "hero-music";

export function useHeroMusicPlayback(covers: AlbumCover[], activeTimelineGroup: HeroMusicTimelineGroupId) {
  const activePlaybackRef = useRef<ActiveAudioPlayback | null>(null);
  const activeTimelineGroupRef = useRef(activeTimelineGroup);
  const playbackOffsetsByPlaybackKeyRef = useRef<Record<string, number | undefined>>({});
  const [playingCoverIndex, setPlayingCoverIndex] = useState<number | null>(null);
  const { audioRef, pause, play, seek, stop } = useNativeAudio();

  activeTimelineGroupRef.current = activeTimelineGroup;

  useEffect(() => {
    return () => {
      activePlaybackRef.current = null;
    };
  }, []);

  function playCover(index: number, seconds?: number, groupId = activeTimelineGroupRef.current) {
    const coverPlayback = getCoverPlayback(index, groupId);

    if (!coverPlayback) return false;

    const { cover, coverIndex, playbackKey, variant } = coverPlayback;
    const audioUrl = variant.audioUrl;

    if (!audioUrl) return false;

    const previousCoverIndex = activePlaybackRef.current?.coverIndex ?? null;
    const fallbackOffset = playbackOffsetsByPlaybackKeyRef.current[playbackKey] ?? 0;
    const startSeconds = getSafeSeekSeconds(variant.timeline.duration, seconds ?? fallbackOffset);

    setActivePlayback({
      coverIndex,
      duration: variant.timeline.duration,
      playbackKey,
    });
    setCoverPlaybackOffset(coverIndex, startSeconds, groupId);

    void play({
      id: playbackKey,
      url: audioUrl,
      startTime: startSeconds,
      onDurationChange: duration => {
        syncActivePlaybackDuration(playbackKey, duration);
      },
      onEnded: () => {
        handlePlaybackEnded(playbackKey);
      },
      onError: () => {
        handlePlaybackError(playbackKey);
      },
      onPlayingChange: isPlaying => {
        if (isPlaying) return;

        handlePlaybackInterrupted(playbackKey);
      },
      onTimeUpdate: (currentTime, duration) => {
        syncActivePlaybackTime(playbackKey, currentTime, duration);
      },
    }).then(() => {
      if (activePlaybackRef.current?.playbackKey !== playbackKey) return;
      if (previousCoverIndex === coverIndex) return;

      captureEvent(EventName.mw_music_hero_sample_played, {
        sample_index: coverIndex,
        sample_id: cover.id,
      });
    });

    return true;
  }

  function toggleCover(index: number) {
    const coverPlayback = getCoverPlayback(index);

    if (!coverPlayback) return false;

    const { cover, coverIndex, playbackKey } = coverPlayback;

    if (activePlaybackRef.current?.playbackKey === playbackKey) {
      setCoverPlaybackOffset(coverIndex, getCurrentAudioTime());
      setActivePlayback(null);
      pause();
      captureEvent(EventName.mw_music_hero_sample_paused, {
        sample_index: coverIndex,
        sample_id: cover.id,
      });
      return false;
    }

    return playCover(coverIndex);
  }

  function seekCover(index: number, seconds: number, options: SeekPlaybackOptions = {}) {
    const coverPlayback = getCoverPlayback(index);

    if (!coverPlayback) return;

    const { coverIndex, playbackKey, variant } = coverPlayback;
    const timelineDuration = variant.timeline.duration;

    if (timelineDuration <= 0) return;

    const seekSeconds = getSafeSeekSeconds(timelineDuration, seconds);
    const isCurrentPlayback = activePlaybackRef.current?.playbackKey === playbackKey;

    setCoverPlaybackOffset(coverIndex, seekSeconds);

    if (!options.play && !isCurrentPlayback) return;

    if (isCurrentPlayback) {
      seek(seekSeconds);
      syncActivePlaybackTime(playbackKey, seekSeconds, timelineDuration);
      return;
    }

    void playCover(coverIndex, seekSeconds);
  }

  function getCoverProgress(index: number) {
    const coverPlayback = getCoverPlayback(index);

    if (!coverPlayback) return 0;

    const timelineDuration = coverPlayback.variant.timeline.duration;

    if (timelineDuration <= 0) return 0;

    return getCoverPlaybackSeconds(coverPlayback.coverIndex) / timelineDuration;
  }

  function stopPlayback(shouldPreserveOffset = true) {
    if (shouldPreserveOffset) {
      preserveActivePlaybackOffset();
    }

    setActivePlayback(null);
    stop();
  }

  function switchTimelineGroup(nextGroupId: HeroMusicTimelineGroupId) {
    const previousGroupId = activeTimelineGroupRef.current;

    if (nextGroupId === previousGroupId) return;

    const activePlayback = activePlaybackRef.current;

    activeTimelineGroupRef.current = nextGroupId;

    if (!activePlayback) return;
    if (!covers[activePlayback.coverIndex]) {
      stopPlayback();
      return;
    }

    const previousDuration = getCoverPlaybackDuration(activePlayback.coverIndex, previousGroupId);
    const nextDuration = getCoverPlaybackDuration(activePlayback.coverIndex, nextGroupId);
    const previousSeconds = getCoverPlaybackSecondsForGroup(activePlayback.coverIndex, previousGroupId);
    const progress = previousDuration > 0 ? previousSeconds / previousDuration : 0;

    void playCover(activePlayback.coverIndex, progress * nextDuration, nextGroupId);
  }

  function getCoverPlayback(index: number, groupId = activeTimelineGroupRef.current) {
    if (covers.length === 0) return null;

    const coverIndex = getWrappedIndex(index, covers.length);
    const cover = covers[coverIndex];

    if (!cover) return null;

    const variant = cover.variants[groupId];

    if (!variant) return null;

    return { cover, coverIndex, playbackKey: getPlaybackKey(cover.id, groupId), variant };
  }

  function setCoverPlaybackOffset(index: number, seconds: number, groupId = activeTimelineGroupRef.current) {
    const coverPlayback = getCoverPlayback(index, groupId);

    if (!coverPlayback) return;

    const { playbackKey, variant } = coverPlayback;

    playbackOffsetsByPlaybackKeyRef.current[playbackKey] = getSafeSeekSeconds(variant.timeline.duration, seconds);
  }

  function getCoverPlaybackSeconds(index: number) {
    return getCoverPlaybackSecondsForGroup(index, activeTimelineGroupRef.current);
  }

  function getCoverPlaybackSecondsForGroup(index: number, groupId: HeroMusicTimelineGroupId) {
    const coverPlayback = getCoverPlayback(index, groupId);

    if (!coverPlayback) return 0;

    const activePlayback = activePlaybackRef.current;

    if (activePlayback?.playbackKey === coverPlayback.playbackKey) {
      return getSafeSeekSeconds(activePlayback.duration, getCurrentAudioTime());
    }

    return playbackOffsetsByPlaybackKeyRef.current[coverPlayback.playbackKey] ?? 0;
  }

  function getCoverPlaybackDuration(index: number, groupId: HeroMusicTimelineGroupId) {
    const coverPlayback = getCoverPlayback(index, groupId);

    if (!coverPlayback) return 0;

    const activePlayback = activePlaybackRef.current;

    if (activePlayback?.playbackKey === coverPlayback.playbackKey) {
      return activePlayback.duration;
    }

    return coverPlayback.variant.timeline.duration;
  }

  function getCurrentAudioTime() {
    return audioRef.current?.currentTime ?? 0;
  }

  function preserveActivePlaybackOffset() {
    const activePlayback = activePlaybackRef.current;

    if (!activePlayback) return;

    playbackOffsetsByPlaybackKeyRef.current[activePlayback.playbackKey] = getSafeSeekSeconds(
      activePlayback.duration,
      getCurrentAudioTime(),
    );
  }

  function setActivePlayback(playback: ActiveAudioPlayback | null) {
    activePlaybackRef.current = playback;
    setPlayingCoverIndex(playback?.coverIndex ?? null);
  }

  function syncActivePlaybackTime(playbackKey: string, currentTime: number, duration: number) {
    const activePlayback = activePlaybackRef.current;

    if (activePlayback?.playbackKey !== playbackKey) return;

    activePlayback.duration = duration;
    playbackOffsetsByPlaybackKeyRef.current[playbackKey] = getSafeSeekSeconds(duration, currentTime);
  }

  function syncActivePlaybackDuration(playbackKey: string, duration: number) {
    const activePlayback = activePlaybackRef.current;

    if (activePlayback?.playbackKey !== playbackKey) return;

    activePlayback.duration = duration;
  }

  function handlePlaybackEnded(playbackKey: string) {
    if (activePlaybackRef.current?.playbackKey !== playbackKey) return;

    setActivePlayback(null);
    playbackOffsetsByPlaybackKeyRef.current[playbackKey] = 0;
  }

  function handlePlaybackError(playbackKey: string) {
    handlePlaybackInterrupted(playbackKey);
  }

  function handlePlaybackInterrupted(playbackKey: string) {
    if (activePlaybackRef.current?.playbackKey !== playbackKey) return;

    setActivePlayback(null);
  }

  return {
    playingCoverIndex,
    playCover,
    toggleCover,
    seekCover,
    getCoverProgress,
    switchTimelineGroup,
    stopPlayback,
  };
}

function getSafeSeekSeconds(duration: number, seekSeconds: number) {
  const maxSeekSeconds = Number.isFinite(duration) && duration > 0 ? duration : 0;

  if (!Number.isFinite(seekSeconds)) return 0;

  return clampValue(seekSeconds, 0, maxSeekSeconds);
}

function getPlaybackKey(coverId: string, groupId: HeroMusicTimelineGroupId) {
  return `${PLAYBACK_KEY_PREFIX}:${coverId}:${groupId}`;
}
